import { useDocumentActive, useInterval, useUpdateEffect } from '@studio/react-utils/hooks';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import { Paths } from './paths.jsx';
import type { Offset, Props } from './types.js';
import { toOffset } from './utils.js';
import { getGamepad } from '#~/gamepad/index.js';

/** @internal */
const getButtons = (buttons: T_Maybe<Gamepad['buttons']>): boolean[] =>
  buttons ? buttons.map((µ) => µ.pressed) : [];

/** @internal */
const getOffsets = (axes: T_Maybe<Gamepad['axes']>): [Offset, Offset] => {
  const normalized: readonly number[] = axes ?? [];

  return [toOffset(normalized, 1), toOffset(normalized, 2)];
};

/** `<Specimen>` component. */
export const Specimen = ({ className, gamepadId, ...props }: Props): ReactElement => {
  const [buttons, setButtons] = useState(() => getButtons(getGamepad(gamepadId)?.buttons));

  const [offsets, setOffsets] = useState<ReturnType<typeof getOffsets>>(() =>
    getOffsets(getGamepad(gamepadId)?.axes)
  );

  const isActive = useDocumentActive();

  const interval = useInterval(
    () => {
      const { axes: a, buttons: b } = getGamepad(gamepadId) ?? {};

      setButtons(getButtons(b));
      setOffsets(getOffsets(a));
    },
    { period: 100, start: isActive }
  );

  useUpdateEffect(() => {
    interval[isActive ? 'start' : 'stop']();
  }, [isActive]);

  return (
    <svg
      className={twMerge(
        'fill-none stroke-muted transition-opacity',
        isActive ? 'opacity-100' : 'opacity-disabled',
        className
      )}
      role='presentation'
      viewBox='0 0 412 264'
      {...props}
    >
      <Paths buttons={buttons} offsets={offsets} />
    </svg>
  );
};
